import {Component} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {BehaviorSubject, Observable, filter, of, switchMap} from 'rxjs';
import {IAccessData} from 'src/app/interfaces/employee.interface';
import {
  IWorkingPeriod,
  WorkingPeriodTimeStatus,
} from 'src/app/interfaces/termination.interface';
import {AccessManagementService} from 'src/app/services/access-management.service';
import {HrDataService} from 'src/app/services/employee-details/hr-data.service';
import {SpinnerOverlayService} from 'src/app/services/spinner.service';
import {ROW_ACTIONS} from 'src/app/shared/utils/constants';
import {roles} from 'src/app/shared/utils/roles';
import {Utils} from 'src/app/shared/utils/utils';
import {
  IHrDataOptions,
  IOneToOne,
  IOneToOneTable,
  IPerformanceReview,
  IPromotion,
  IValuesHobbies,
} from '../../../interfaces/hr-data.interface';
import {DialogService} from '../../../services/dialog.service';
import {
  ITableRowAction,
  UITableHeader,
  columnTypes,
} from '../../../shared/ui-kits/table/table.models';
import {OneToOneComponent} from './one-to-one/one-to-one.component';
import {PerformanceReviewComponent} from './performance-review/performance-review.component';
import {PromotionComponent} from './promotion/promotion.component';
import {ValuesAndHobbiesComponent} from './values-and-hobbies/values-and-hobbies.component';

/**
 * The hr data component
 *
 * Display hr data
 */

@UntilDestroy()
@Component({
  selector: 'app-hr-data',
  templateUrl: './hr-data.component.html',
  styleUrls: ['./hr-data.component.scss'],
})
export class HrDataComponent {
  /** isEditable property to activate edit mode on general tab */
  public isEditable = false;
  public show = false;
  public typeOfTable = 'material-plus';
  public employeeId!: number;

  /** data for Performance Review popup */

  public headersCertPerformanceReview: UITableHeader[] = [
    {key: 'authorID', index: 0, notVisible: true},
    {key: 'date', index: 1, type: columnTypes.datePicker},
    {key: 'employeeId', index: 2, notVisible: true},
    {key: 'hr', index: 4, type: columnTypes.dropDown},
    {key: 'hrId', index: 5, notVisible: true},
    {key: 'id', index: 6, notVisible: true},
    {key: 'pm', index: 7, type: columnTypes.dropDown},
    {key: 'pmId', index: 8, notVisible: true},
    {key: 'comment', index: 9},
    {key: 'status', index: 10, type: columnTypes.dropDown},
    {key: 'level', index: 3, type: columnTypes.dropDown},
    {key: 'file', index: 11, type: columnTypes.link},
  ];

  public dataCertPerformanceReview: IPerformanceReview[] = [];

  /** data for Promotion popup */

  public headersCertPromotion: UITableHeader[] = [
    {key: 'date', index: 0},
    {key: 'employeeId', index: 1, notVisible: true},
    {key: 'id', index: 2, notVisible: true},
    {key: 'level', index: 3, type: columnTypes.dropDown},
    {key: 'stackTechnology', index: 4, type: columnTypes.dropDown},
    {key: 'role', index: 5, type: columnTypes.dropDown, notVisible: true},
    {key: 'position', index: 6, type: columnTypes.dropDown},
    {key: 'comment', index: 7},
    {key: 'status', index: 8, type: columnTypes.dropDown},
    {key: 'pm', index: 9, notVisible: true, type: columnTypes.dropDown},
    {key: 'hr', index: 10, notVisible: true, type: columnTypes.dropDown},
    {key: 'department', index: 11, notVisible: true},
    {key: 'file', index: 12, type: columnTypes.link},
  ];

  public dataCertPromotion: IPromotion[] = [];

  /** data for OneToOne popup */

  headersCertOneToOne: UITableHeader[] = [
    {key: 'date', index: 0, type: columnTypes.datePicker},
    {key: 'createdBy', index: 12},
    {key: 'hr', index: 1, type: columnTypes.dropDown},
    {key: 'pm', index: 2, type: columnTypes.dropDown, displayedName: 'PM/LM'},
    {key: 'type', index: 3, type: columnTypes.dropDown},
    {key: 'oneToOneTopics', index: 4, notVisible: true},

    {key: 'riskLevel', index: 5, type: columnTypes.dropDown},
    {key: 'topic', index: 6, type: columnTypes.dropDown},
    {key: 'topicComment', index: 7, type: columnTypes.topicComment},
    {
      key: 'topicDetails',
      index: 8,
      type: columnTypes.topicTable,
    },
    {key: 'employeeId', index: 9, notVisible: true},
    {key: 'id', index: 10, notVisible: true},
    {key: 'generalComment', index: 11, dynamicColumn: true},
    {key: 'actionItems', index: 12, dynamicColumn: true},
  ];

  public dataCertWorkingPeriods: IWorkingPeriod[] = [];

  headersCertWorkingPeriods: UITableHeader[] = [
    {key: 'newStatus', index: 0, type: columnTypes.dropDown},
    {key: 'lastWorkingDay', index: 1, type: columnTypes.datePicker},
    {
      key: 'startDate',
      index: 6,
      type: columnTypes.datePicker,
      displayedName: 'New start date',
    },
    {key: 'terminationReason', index: 2, type: columnTypes.dropDown},
    {key: 'isVoluntary', index: 3},
    {key: 'corporateAccount', index: 4, type: columnTypes.dropDown},
    {key: 'accountCorrespondence', index: 5, type: columnTypes.dropDown},
    {key: 'equipment', index: 6, type: columnTypes.dropDown},
    {key: 'status', index: 6},
  ];

  public dataCertOneToOne: IOneToOneTable[] = [];
  public isHrManager = false;
  public initialValues!: IHrDataOptions;
  public ableToEdit: boolean = true;
  public isRecruiter!: boolean;
  public isAccountant!: boolean;
  public isLineManager!: boolean;
  public isDeliveryDepartment!: boolean;
  public createEmptyObject = Utils.createEmptyObject;

  private employeeValuesAndHobbies!: IValuesHobbies;
  private readonly performanceReviewListChanged = new BehaviorSubject<void>(
    undefined
  );
  private readonly promotionListChanged = new BehaviorSubject<void>(undefined);
  private readonly oneToOneListChanged = new BehaviorSubject<void>(undefined);
  readonly ROW_ACTIONS = ROW_ACTIONS;

  constructor(
    private dialogService: DialogService,
    private route: ActivatedRoute,
    private access: AccessManagementService,
    private router: Router,
    private spinner: SpinnerOverlayService,
    private hrDataService: HrDataService
  ) {}

  ngOnInit() {
    this.employeeId = +this.route.parent!.snapshot.params['id'];

    if (
      !this.access.hasHRDataAccess(
        this.route.parent?.snapshot.data['accessData'] as IAccessData
      )
    ) {
      this.router.navigate(['profile', this.employeeId, 'general']);
      this.spinner.clearLoadingState();

      return;
    }

    this.getWorkingPeriods();
    this.getValuesAndHobbies();
    this.subscribeOnPerformanceReviewStream();
    this.subscribePromotionsStream();
    this.subscribeOnOneToOnesStream();

    this.hrDataService
      .getHrDataOptions(this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe((response: IHrDataOptions) => {
        this.initialValues = response;
      });

    this.isHrManager = this.access.checkPermissions(roles.HR_DEPARTMENT);
    this.isAccountant = this.access.checkPermissions(
      roles.ACCOUNTANT_DEPARTMENT
    );
    this.isRecruiter = this.access.checkPermissions(
      roles.RECRUITMENT_DEPARTMENT
    );
    this.isLineManager = this.access.checkPermissions(roles.LINE_MANAGEMENT);

    if ((this.isRecruiter || this.isAccountant) && !this.isLineManager) {
      this.ableToEdit = false;
    }

    this.isDeliveryDepartment = this.access.checkPermissions(
      roles.DELIVERY_DEPARTMENT
    );
  }
  /**
   * edit mode for hr data tab, isn't activated by default
   * @param {Event} event to handle edit mode
   */

  switchEdit(event: Event) {
    event.preventDefault();
    this.isEditable = !this.isEditable;
  }

  performanceReviewTableActions(event: ITableRowAction) {
    switch (event.action) {
      case this.ROW_ACTIONS.ADD:
        this.addNewPerformanceReview();
        break;
      case this.ROW_ACTIONS.EDIT:
        const performanceReview = this.dataCertPerformanceReview[event.index];
        this.updatePerformanceReview(performanceReview);
        break;
    }
  }
  promotionTableActions(event: ITableRowAction) {
    switch (event.action) {
      case this.ROW_ACTIONS.ADD:
        this.addNewPromotion();
        break;
      case this.ROW_ACTIONS.EDIT:
        const promotion = this.dataCertPromotion[event.index];
        this.updatePromotion(promotion);
        break;
    }
  }

  private transformWorkingPeriods(
    workingPeriods: IWorkingPeriod[]
  ): IWorkingPeriod[] {
    return workingPeriods.map(workingPeriod => ({
      ...workingPeriod,
      status: this.getWorkingPeriodStatus(workingPeriod),
    }));
  }

  getWorkingPeriodStatus(workingPeriod: IWorkingPeriod): string {
    if (workingPeriod.isCancelled) return WorkingPeriodTimeStatus.Cancelled;

    const startDate = workingPeriod.startDate;
    if (
      startDate &&
      Utils.formatStringToDate(startDate).getTime() >
        new Date().setHours(0, 0, 0, 0)
    )
      return WorkingPeriodTimeStatus.Pending;
    return WorkingPeriodTimeStatus.Conducted;
  }

  transformWorkingPeriodStartDate() {
    if (this.dataCertWorkingPeriods && this.dataCertWorkingPeriods.length)
      this.dataCertWorkingPeriods.forEach(wp => {
        wp.startDate = wp.startDate || '-';
      });
  }

  openValuesAndHobbies() {
    this.dialogService
      .open(ValuesAndHobbiesComponent, {
        data: this.employeeValuesAndHobbies,
        settings: {
          width: '580px',
          height: '365px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data => this.hrDataService.updateValuesAndHobbies(data))
      )
      .subscribe(response => (this.employeeValuesAndHobbies = response));
  }

  oneToOneTableActions(event: ITableRowAction) {
    switch (event.action) {
      case this.ROW_ACTIONS.ADD:
        this.addNewOneToOne();
        break;
      case this.ROW_ACTIONS.EDIT:
        const associatedOneToOnes = this.dataCertOneToOne.find(
          v => v.id === this.dataCertOneToOne[event.index].id
        );
        if (associatedOneToOnes) {
          this.updateOneToOne(associatedOneToOnes);
        }
        break;
    }
  }

  addNewPerformanceReview() {
    const emptyData = this.createEmptyObject(
      this.headersCertPerformanceReview,
      this.employeeId
    );

    this.dialogService
      .open(PerformanceReviewComponent, {
        data: {
          content: emptyData,
          statusDropdown: this.initialValues.options.ReviewStatus,
          pm: this.initialValues.options.PM,
          hr: this.initialValues.options.HR,
          level: this.initialValues.options.QualificationLevel,
        },
        settings: {
          width: '788px',
          height: '386px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data => this.hrDataService.addPerformanceReview(data))
      )
      .subscribe(() => this.performanceReviewListChanged.next());
  }

  updatePerformanceReview(performanceReview: IPerformanceReview) {
    this.dialogService
      .open(PerformanceReviewComponent, {
        data: {
          content: performanceReview,
          statusDropdown: this.initialValues.options.ReviewStatus,
          pm: this.initialValues.options.PM,
          hr: this.initialValues.options.HR,
          level: this.initialValues.options.QualificationLevel,
        },
        settings: {
          width: '788px',
          height: '386px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data =>
          data === this.ROW_ACTIONS.DELETED
            ? of(null)
            : this.hrDataService.updatePerformanceReview(data)
        )
      )
      .subscribe(() => {
        this.performanceReviewListChanged.next();
      });
  }

  addNewPromotion() {
    const emptyData = this.createEmptyObject(
      this.headersCertPromotion,
      this.employeeId
    );

    this.dialogService
      .open(PromotionComponent, {
        data: {
          value: emptyData,
          dropdowns: this.initialValues.options,
        },
        settings: {
          width: '788px',
          height: 'auto',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data => this.hrDataService.addPromotion(data))
      )
      .subscribe(() => {
        this.promotionListChanged.next();
      });
  }

  updatePromotion(promotion: IPromotion) {
    this.dialogService
      .open(PromotionComponent, {
        data: {
          value: promotion,
          dropdowns: this.initialValues.options,
        },
        settings: {
          width: '788px',
          height: 'auto',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data =>
          data === this.ROW_ACTIONS.DELETED
            ? of(null)
            : this.hrDataService.updatePromotion(data)
        )
      )
      .subscribe(() => {
        this.promotionListChanged.next();
      });
  }

  addNewOneToOne() {
    const emptyData = this.createEmptyObject(
      this.headersCertOneToOne,
      this.employeeId
    );

    this.dialogService
      .open(OneToOneComponent, {
        data: {
          value: emptyData,
          dropdowns: this.initialValues.options,
        },
        settings: {
          width: '788px',
          height: '500px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data => this.hrDataService.addOneToOne(data))
      )
      .subscribe(() => {
        this.oneToOneListChanged.next();
      });
  }

  updateOneToOne(oneToOnes: IOneToOne) {
    this.dialogService
      .open(OneToOneComponent, {
        data: {
          value: oneToOnes,
          dropdowns: this.initialValues.options,
        },
        settings: {
          width: '788px',
          height: '500px',
        },
      })
      .afterClosed()
      .pipe(
        untilDestroyed(this),
        filter(data => !!data),
        switchMap(data =>
          data === this.ROW_ACTIONS.DELETED
            ? of(null)
            : this.hrDataService.updateOneToOne(data)
        )
      )
      .subscribe(() => this.oneToOneListChanged.next());
  }

  handleOneToOneData(res: IOneToOne[]): void {
    const tableData = [];
    for (const oneToOne of res) {
      if (!oneToOne.oneToOneTopics.length) {
        tableData.push(oneToOne);
      } else {
        for (const topic of oneToOne.oneToOneTopics) {
          const row: IOneToOneTable = {...oneToOne};
          row.topic = topic.topic;
          row.topicComment = topic.topicComment;
          row.topicDetails = {
            important: topic.importanceLevel ? topic.importanceLevel.name : '',
            satisfaction: topic.satisfactionLevel
              ? topic.satisfactionLevel.name
              : '',
            status: topic.status ? topic.status.name : '',
          };
          tableData.push(row);
        }
      }
    }
    this.dataCertOneToOne = tableData;
  }

  private getPerformanceReviews(): Observable<IPerformanceReview[]> {
    return this.hrDataService.getPerformanceReviews(this.employeeId);
  }

  private subscribeOnPerformanceReviewStream() {
    this.performanceReviewListChanged
      .pipe(
        untilDestroyed(this),
        switchMap(() => this.getPerformanceReviews())
      )
      .subscribe(
        performanceReviews =>
          (this.dataCertPerformanceReview = performanceReviews)
      );
  }

  private getOneToOnes(): Observable<IOneToOne[]> {
    return this.hrDataService.getOneToOnes(this.employeeId);
  }

  private subscribeOnOneToOnesStream() {
    this.oneToOneListChanged
      .pipe(
        untilDestroyed(this),
        switchMap(() => this.getOneToOnes())
      )
      .subscribe(oneToOnes => {
        this.handleOneToOneData(oneToOnes);
      });
  }
  private getPromotions(): Observable<IPromotion[]> {
    return this.hrDataService.getPromotions(this.employeeId);
  }

  private subscribePromotionsStream() {
    this.promotionListChanged
      .pipe(
        untilDestroyed(this),
        switchMap(() => this.getPromotions())
      )
      .subscribe(promotions => {
        this.dataCertPromotion = promotions;
      });
  }

  private getWorkingPeriods() {
    this.hrDataService
      .getWorkingPeriods(this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe(workingPeriods => {
        this.dataCertWorkingPeriods =
          this.transformWorkingPeriods(workingPeriods);
        this.transformWorkingPeriodStartDate();
      });
  }

  private getValuesAndHobbies() {
    this.hrDataService
      .getValuesAndHobbies(this.employeeId)
      .pipe(untilDestroyed(this))
      .subscribe(res => {
        this.employeeValuesAndHobbies = res;
      });
  }
}
